import clsx from 'clsx'
import Link from 'next/link'
import type { FunctionComponent } from 'react'
import { InternalLink } from '../../const/Links'
import { rephraseSize } from '../../const/Rephrase'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { ContentTextBlockResult } from '../../data/ContentTextFragment'
import type { ItemSubCategorySummaryResult } from '../../data/ItemSubCategoryFragment'
import type { ProductDetailResult } from '../../data/ProductFragment'
import { Container } from '../shared/Container'
import { IconItemGroup } from '../shared/IconItemGroup'
import { ItemAttribute } from '../shared/ItemAttribute'
import { SummaryArticle } from '../shared/SummaryArticle'
import style from './SummaryTab.module.sass'

type SummaryTabProps = {
	product: ProductDetailResult
	excludedFeatures: ItemSubCategorySummaryResult[]
	vetDietInfo: ContentTextBlockResult[] | undefined
}

export const SummaryTab: FunctionComponent<SummaryTabProps> = ({ product, vetDietInfo, excludedFeatures }) => {
	const translate = useTranslate()
	return (
		<>
			<Container size="narrow" variant="leftStickyAligned" disableGutters>
				<div className={style.summaryArticleWrapper}>
					<SummaryArticle
						production={product?.brand?.methodOfProduction?.name}
						isDiet={product?.isVeterinaryDiet}
						firstFiveItems={product?.firstFiveItems}
						vetDietInfo={vetDietInfo}
						size={
							product?.recommendedDogSizes.length > 0 ? (
								<span className={style.noTransform}>{rephraseSize(product.granuleSizes.map((size) => size.name))}</span>
							) : undefined
						}
						ingredientCategories={
							product?.itemCategories.length > 0
								? product.itemCategories.map((category) => (
										<Link
											target="_blank"
											key={category.name}
											title={category.name}
											href={`${InternalLink.IngredientCategory}${category.slug}`}
											className={style.categoryLink}
										>
											{category.name}
										</Link>
									))
								: undefined
						}
					/>
				</div>

				{product.guaranteedAnalysis && (
					<div className={style.nutritionValuesWrapper}>
						<IconItemGroup title={translate('summaryTab.nutritionalValues.title')} items={product.nutritionalValues} />
					</div>
				)}
			</Container>
			<Container size="wide" disableGutters>
				{excludedFeatures.length > 0 && product.detailedComposition && product.detailedComposition !== '0' && (
					<div className={style.infoBlock}>
						<h2 className={style.infoTitle}>
							<Translation tr="summaryTab.allergens.title" />:
						</h2>
						<div className={style.infoContent}>
							{excludedFeatures.map(
								(feature) =>
									feature.iconNegative?.url && (
										<ItemAttribute
											key={feature.id}
											iconUrl={feature.iconNegative?.url}
											title={feature.negativeWording ?? ''}
										/>
									),
							)}
						</div>
					</div>
				)}
				{product.positiveForBodyTags.length > 0 && (
					<div className={style.infoBlock}>
						<h2 className={clsx(style.infoTitle, style.greenColor)}>
							<Translation tr="summaryTab.positiveForBodyTags.title" />
						</h2>
						<div className={style.infoContent}>
							{product?.positiveForBodyTags.map((tag) => (
								<ItemAttribute key={tag.id} iconUrl={tag.icon?.url} title={tag.name} />
							))}
						</div>
					</div>
				)}
				{product.negativeForBodyTags.length > 0 && (
					<div className={style.infoBlock}>
						<h2 className={clsx(style.infoTitle, style.redColor)}>
							<Translation tr="summaryTab.negativeForBodyTags.title" />
						</h2>
						<div className={style.infoContent}>
							{product.negativeForBodyTags.map((tag) => (
								<ItemAttribute key={tag.id} iconUrl={tag.icon?.url} title={tag.name} />
							))}
						</div>
					</div>
				)}
			</Container>
		</>
	)
}
